import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import axios from 'axios';

const makeInputSx = (dark) => ({
  mb: 2,
  '& .MuiOutlinedInput-root': {
    color: dark ? '#fff' : '#161616',
    borderRadius: 2,
    backgroundColor: dark ? 'transparent' : '#f7f7f7',
    '& fieldset': { borderColor: dark ? 'rgba(142,191,99,0.3)' : 'rgba(0,0,0,0.15)' },
    '&:hover fieldset': { borderColor: '#8ebf63' },
    '&.Mui-focused fieldset': { borderColor: '#8ebf63' },
  },
  '& .MuiInputLabel-root': { color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#39a82d' },
});

/**
 * Formulario de cambio de contraseña reutilizable.
 *
 * Props:
 *   onSuccess()  — se llama cuando el cambio fue exitoso
 *   onCancel()   — se llama cuando el usuario cancela
 *   dark         — usa paleta oscura (default false)
 *   showCancel   — muestra el botón cancelar (default true)
 */
export default function CambioClaveForm({ onSuccess, onCancel, dark = false, showCancel = true }) {
  const [passwordact, setPasswordact]   = useState('');
  const [passwordnew, setPasswordnew]   = useState('');
  const [passwordnewr, setPasswordnewr] = useState('');
  const [error, setError]               = useState('');
  const [showError, setShowError]       = useState(false);
  const [loading, setLoading]           = useState(false);

  const inputSx = makeInputSx(dark);

  const showErr = (msg) => {
    setError(msg);
    setShowError(true);
  };

  const handleConfirmar = async () => {
    if (passwordnew !== passwordnewr) {
      showErr('Las nuevas contraseñas no coinciden');
      return;
    }
    if (passwordnew === passwordact) {
      showErr('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);
    try {
      const username = localStorage.getItem('usuario');

      // Verificar contraseña actual
      let usernamedb = '';
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/login`, {
          usr_codigo: username,
          usr_clave: passwordact,
        });
        usernamedb = res.data.usr_codigo;
      } catch {
        usernamedb = '';
      }

      if (usernamedb !== username) {
        showErr('La contraseña actual es incorrecta');
        setLoading(false);
        return;
      }

      // Cambiar contraseña
      await fetch(`${process.env.REACT_APP_API_URL}/resetpass/${username}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('token'),
        },
        body: JSON.stringify({ usr_clave: passwordnewr }),
      });

      if (localStorage.getItem('cambio') === 'S') {
        localStorage.setItem('cambio', 'N');
      }

      onSuccess && onSuccess();
    } catch (err) {
      showErr('Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TextField
        label="Contraseña Actual"
        type="password"
        fullWidth
        sx={inputSx}
        value={passwordact}
        onChange={(e) => setPasswordact(e.target.value)}
      />
      <TextField
        label="Nueva Contraseña"
        type="password"
        fullWidth
        sx={inputSx}
        value={passwordnew}
        onChange={(e) => setPasswordnew(e.target.value)}
      />
      <TextField
        label="Repetir Contraseña"
        type="password"
        fullWidth
        sx={{ ...inputSx, mb: 3 }}
        value={passwordnewr}
        onChange={(e) => setPasswordnewr(e.target.value)}
      />

      <Grid container spacing={2}>
        <Grid item xs={showCancel ? 7 : 12}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirmar}
            disabled={loading}
            sx={{
              backgroundColor: '#8ebf63',
              color: dark ? '#161616' : '#fff',
              fontFamily: "'Nohemi', Arial, sans-serif",
              fontWeight: 700,
              fontSize: '0.9rem',
              letterSpacing: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'uppercase',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#39a82d', color: '#fff', boxShadow: 'none' },
            }}
          >
            CONFIRMAR
          </Button>
        </Grid>
        {showCancel && (
          <Grid item xs={5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              sx={{
                borderColor: '#8ebf63',
                color: dark ? '#8ebf63' : '#39a82d',
                fontFamily: "'Nohemi', Arial, sans-serif",
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: 1,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'uppercase',
                '&:hover': {
                  borderColor: '#39a82d',
                  backgroundColor: 'rgba(57,168,45,0.08)',
                },
              }}
            >
              CANCELAR
            </Button>
          </Grid>
        )}
      </Grid>

      <Collapse in={showError}>
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setShowError(false)}>
          {error}
        </Alert>
      </Collapse>
    </>
  );
}
